import React, { useEffect, useState } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  CardMedia,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import logo from "../imgs/wandr-logo.svg";
import BottomNav from "./BottomNav";
import { useNavigate } from "react-router-dom";
import { useListenForNewMessages } from "./listenForNotifications";
import LogoLoader from "./LogoLoader";
import PlacesMap from "./PlacesMap";
import HomeSearch from "./HomeSearch";
import { CONTINENTS } from "../placeSearch";
import { paperSx } from "./PlaceAutocomplete";

const WORLD_VIEW = { location: { lat: 20, lng: 0 }, zoom: 2 };

const Home = () => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapFocus, setMapFocus] = useState(null);
  const [selectedContinent, setSelectedContinent] = useState("");
  const navigate = useNavigate();

  useListenForNewMessages();

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        setLoading(true);
        const placesCollectionRef = collection(db, "places");
        const unsubscribe = onSnapshot(
          query(placesCollectionRef, orderBy("createdAt", "desc")),
          (snapshot) => {
            const placesData = snapshot.docs.map((doc) => {
              const data = doc.data();
              console.log("Raw Firestore data:", data);

              let imageUrls = [];
              if (data.images && Array.isArray(data.images)) {
                imageUrls = data.images;
              } else if (data.images && typeof data.images === "string") {
                imageUrls = [data.images];
              }

              console.log("Processed imageUrls:", imageUrls);

              return {
                id: doc.id,
                name: data.name,
                title: data.title,
                description: data.description,
                price: data.price,
                city: data.city,
                place: data.place,
                address: data.address,
                province: data.province,
                country: data.country,
                lat: data.lat,
                lng: data.lng,
                googlePlaceId: data.googlePlaceId,
                claimStatus: data.claimStatus,
                imageUrls: imageUrls,
                createdAt: data.createdAt,
              };
            });
            console.log("Processed placesData:", placesData);
            setLoading(false);
            setPlaces(placesData);
          }
        );
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener alojamientos:", error);
        setLoading(false);
      }
    };
    fetchPlaces();
  }, []);

  const handleContinentChange = (e) => {
    setSelectedContinent(e.target.value);
    // "Todos" on the map means the whole world.
    if (!e.target.value) setMapFocus({ ...WORLD_VIEW });
  };

  // On the map, both the search box and the continent move the view instead of hiding pins.
  const handleSearchSelect = (focus) => {
    setSelectedContinent("");
    setMapFocus(focus);
  };

  const handleVerClick = (placeId) => {
    navigate(`/ver/${placeId}`);
  };

  const styles = {
    cont: {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      margin: 0,
      padding: 0,
      boxSizing: "border-box",
    },
    img: {
      maxWidth: "150px",
      maxHeight: "40px",
      display: "block",
      margin: "20px auto",
    },
    slogan: {
      color: "white",
      fontSize: "14px",
      fontWeight: "bold",
      paddingBottom: "-5px",
    },
    iconContainer: {
      display: "flex",
      width: "100%",
      borderTop: "1.5px solid black",
      justifyContent: "space-around",
      color: "black",
    },
    iconContainerL: {
      display: "flex",
      width: "100%",
      borderTop: "1.5px solid black",
      justifyContent: "space-around",
    },
    backSlogan: {
      display: "block",
      width: "100vw",
      height: "19px",
      backgroundColor: "#fff",
      textAlign: "center",
    },
    listItem: {
      display: "block",
      width: "95%",
      border: "0.5px solid #ccc",
      margin: "15px auto",
      boxShadow: "0px 0px 3px 0px black",
    },
    typography: {
      marginBottom: "0px",
    },
    contList: {
      marginTop: "-30px",
    },
    inputS: {
      width: "95%",
      height: "35px",
      marginTop: "20px",
      padding: "5px",
      fontSize: "14px",
      border: "3px solid grey",
      borderRadius: "4px",
    },
    buscar: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      width: "100%",
      marginBottom: "5px",
    },
    card: {
      border: "2px solid grey",
      borderRadius: "10px",
      padding: "20px",
    },
    carde: {
      width: "100%",
      border: "1px solid #ddd",
      borderRadius: "8px",
      overflow: "hidden",
      marginBottom: "20px",
    },
    imageContainer: {
      position: "relative",
      width: "100%",
      height: "300px",
    },
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    additionalImagesOverlay: {
      position: "absolute",
      bottom: "10px",
      right: "10px",
      width: "80px",
      height: "80px",
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      color: "white",
      cursor: "pointer",
    },
    additionalImagesCount: {
      fontSize: "24px",
      fontWeight: "bold",
    },
    contentContainer: {
      padding: "16px",
    },
    name: {
      fontSize: "14px",
      color: "#777",
      marginBottom: "8px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "bold",
      marginBottom: "8px",
    },
    description: {
      fontSize: "14px",
      marginBottom: "8px",
    },
    price: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#4CAF50",
      marginBottom: "8px",
    },
    place: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#34d399",
      marginBottom: "8px",
    },
    created: {
      fontSize: "12px",
      color: "#999",
    },
    media: {
      width: "70px",
      marginTop: "15px",
      marginBottom: "15px",
    },
    imageGrid: {
      marginTop: "15px",
      marginBottom: "15px",
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    noImage: {
      width: "100%",
      height: "100px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      color: "#9fd2b7",
    },
  };

  return (
    // No max width so the map can use the full screen on desktop.
    <Container maxWidth={false}>
      <AppBar
        position="fixed"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          marginBottom: "10px",
          bgcolor: "black",
        }}
      >
        {/* On desktop the logo lives in the sidebar instead. */}
        <Toolbar
          sx={{
            display: { xs: "flex", md: "none" },
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <CardMedia
            component="img"
            image={logo}
            title="Wandr"
            style={styles.media}
          />
        </Toolbar>
        {/* Without the logo row above (desktop), give the search field room for its floating label. */}
        <Toolbar sx={{ pt: { md: 2 }, pb: { md: 1 } }}>
          <Box sx={styles.buscar}>
            <HomeSearch
              places={places}
              onSelect={handleSearchSelect}
              sx={{
                width: "70%",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#fff",
                  },
                  "&:hover fieldset": {
                    borderColor: "#fff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#fff",
                  },
                  "& input": {
                    color: "#fff",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#fff",
                  "&.Mui-focused": {
                    color: "#fff",
                  },
                },
              }}
            />
            <TextField
              select
              label="Continente"
              value={selectedContinent}
              onChange={handleContinentChange}
              variant="outlined"
              SelectProps={{ MenuProps: { PaperProps: { sx: paperSx } } }}
              sx={{
                width: "28%",
                ml: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#fff",
                  },
                  "&:hover fieldset": {
                    borderColor: "#fff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#fff",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#fff",
                  "&.Mui-focused": {
                    color: "#fff",
                  },
                },
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {CONTINENTS.map((continent) => (
                <MenuItem key={continent} value={continent}>
                  {continent}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} sx={{ mt: { xs: 25, md: 15.5 }, mb: { xs: 12, md: 3 } }}>
        {loading ? (
          <LogoLoader label="Cargando alojamientos..." minHeight={300} />
        ) : (
          <PlacesMap
            places={places}
            continent={selectedContinent}
            focus={mapFocus}
            onOpenPlace={handleVerClick}
            onClaimPlace={(googlePlaceId) => navigate(`/publish?googlePlaceId=${googlePlaceId}`)}
            // Fill the space between the top bar and the bottom nav (no bottom nav on desktop).
            sx={{ height: { xs: "calc(100dvh - 290px)", md: "calc(100dvh - 148px)" }, minHeight: 420 }}
          />
        )}
      </Container>
      <BottomNav />
    </Container>
  );
};

export default Home;